import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck, RefreshCw, ChevronDown, ChevronRight, CheckCircle,
  AlertCircle, Loader2, Pencil, History as HistoryIcon
} from 'lucide-react';
import {
  receiptReviewService, ReceiptReviewDto, ReceiptReviewLineDto, ReceiptReviewAdjustmentDto
} from '../../../../services/receiptReviewService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { BackButton } from '../../../common/BackButton';
import { NumericInput } from '../../../common/inputs';

interface ReceiptReviewProps {
  onBack: () => void;
}

/**
 * Etterkontroll mottak (Teamwork 36333932) — KVALITET-seksjonen i Varemottak-gruppen.
 *
 * Mottak som krever kontroll (receiving.requireReviewBeforeExport) holdes tilbake fra
 * ERP-eksport. Her kontrollerer administrator antallene, justerer ved behov (logges —
 * GO-forenkling: ingen korreksjonstransaksjoner) og godkjenner — godkjenningen trigger
 * nøyaktig samme eksport som normalflyten.
 */
export const ReceiptReview: React.FC<ReceiptReviewProps> = ({ onBack }) => {
  const { t } = useTranslation();
  // Valgt lager (null = alle lager) — samme filter som Utlevering. Låst profil håndheves i backend.
  const { currentWarehouse } = useWarehouse();
  const [reviews, setReviews] = useState<ReceiptReviewDto[]>([]);
  const [includeApproved, setIncludeApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [lines, setLines] = useState<ReceiptReviewLineDto[]>([]);
  const [adjustments, setAdjustments] = useState<ReceiptReviewAdjustmentDto[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);

  // Justering via modal (samme lærdom som plukk: aldri skjør inline-tekst i tabell)
  const [adjustingLine, setAdjustingLine] = useState<ReceiptReviewLineDto | null>(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [isSavingAdjust, setIsSavingAdjust] = useState(false);

  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await receiptReviewService.getReviews(includeApproved, currentWarehouse || undefined);
      setReviews(data);
    } catch (err: any) {
      setError(err.message || t('manager.modules.receiptReview.loadError', {}, 'Kunne ikke laste kontrollkøen'));
    } finally {
      setLoading(false);
    }
  }, [includeApproved, currentWarehouse, t]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  // Lagerbytte: lukk åpen kontroll — den kan tilhøre et lager som ikke lenger vises
  useEffect(() => { setExpandedId(null); }, [currentWarehouse]);

  const loadDetails = useCallback(async (reviewId: number) => {
    setLinesLoading(true);
    try {
      const [lineData, adjData] = await Promise.all([
        receiptReviewService.getLines(reviewId),
        receiptReviewService.getAdjustments(reviewId)
      ]);
      setLines(lineData);
      setAdjustments(adjData);
    } catch (err) {
      console.error('Error loading review details:', err);
      setLines([]);
      setAdjustments([]);
    } finally {
      setLinesLoading(false);
    }
  }, []);

  const handleToggleExpand = (review: ReceiptReviewDto) => {
    if (expandedId === review.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(review.id);
    loadDetails(review.id);
  };

  const handleAdjustConfirm = async () => {
    if (!adjustingLine || expandedId === null) return;
    const newQty = parseFloat(adjustQty);
    if (isNaN(newQty) || newQty < 0) return;

    setIsSavingAdjust(true);
    try {
      await receiptReviewService.adjustLine(expandedId, adjustingLine.lineID, newQty, adjustNote || undefined);
      setAdjustingLine(null);
      await Promise.all([loadDetails(expandedId), loadReviews()]);
    } catch (err: any) {
      alert(err.message || t('manager.modules.receiptReview.adjustFailed', {}, 'Justeringen feilet'));
    } finally {
      setIsSavingAdjust(false);
    }
  };

  const handleApprove = async (review: ReceiptReviewDto) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('manager.modules.receiptReview.confirmApprove', { '0': review.orderNoERP || review.batchID },
      `Godkjenn mottaket for ordre ${review.orderNoERP || review.batchID}? Mottaket eksporteres til ERP umiddelbart.`))) {
      return;
    }
    setApprovingId(review.id);
    setSuccessMessage(null);
    try {
      const result = await receiptReviewService.approve(review.id);
      setSuccessMessage(t('manager.modules.receiptReview.approveSuccess',
        { '0': review.orderNoERP || review.batchID, '1': `${result.filesExported}` },
        `Mottak for ordre ${review.orderNoERP || review.batchID} godkjent — ${result.filesExported} fil(er) eksportert til ERP`));
      setExpandedId(null);
      await loadReviews();
    } catch (err: any) {
      alert(err.message || t('manager.modules.receiptReview.approveFailed', {}, 'Godkjenningen feilet — mottaket står fortsatt i køen'));
    } finally {
      setApprovingId(null);
    }
  };

  const adjustedLineIds = new Set(adjustments.map(a => a.stcTransLineID));

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <BackButton onClick={onBack} />
        <ClipboardCheck size={22} className="text-orange-600" />
        <h1 className="text-lg font-semibold text-gray-900">
          {t('manager.modules.receiptReview.title', {}, 'Etterkontroll mottak')}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeApproved}
              onChange={(e) => setIncludeApproved(e.target.checked)}
              className="rounded text-orange-600 focus:ring-orange-500"
            />
            {t('manager.modules.receiptReview.showApproved', {}, 'Vis godkjente')}
          </label>
          <button
            onClick={loadReviews}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('common.buttons.refresh', {}, 'Oppdater')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <ClipboardCheck size={40} className="mx-auto mb-2" />
            <p className="text-sm">{t('manager.modules.receiptReview.emptyQueue', {}, 'Ingen mottak venter på kontroll')}</p>
          </div>
        ) : (
          reviews.map(review => {
            const isExpanded = expandedId === review.id;
            const isPending = review.status === 0;
            return (
              <div key={review.id} className="bg-white rounded-lg shadow border border-gray-200">
                <div
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleToggleExpand(review)}
                >
                  {isExpanded ? <ChevronDown size={16} className="text-orange-500" /> : <ChevronRight size={16} className="text-gray-400" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{review.orderNoERP || review.batchID}</span>
                      <span className="text-sm text-gray-600 truncate">{review.supplierName || ''}</span>
                      {isPending ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {t('manager.modules.receiptReview.statusPending', {}, 'Venter på kontroll')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('manager.modules.receiptReview.statusApproved', {}, 'Godkjent')}
                          {review.approvedAt && ` ${new Date(review.approvedAt).toLocaleString('nb-NO')}`}
                        </span>
                      )}
                      {review.adjustmentCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Pencil size={10} />
                          {t('manager.modules.receiptReview.adjustments', { '0': `${review.adjustmentCount}` }, `${review.adjustmentCount} justering(er)`)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {t('manager.modules.receiptReview.receivedBy', { '0': review.receivedBy || '-' }, `Mottatt av ${review.receivedBy || '-'}`)}
                      {' · '}{new Date(review.created).toLocaleString('nb-NO')}
                      {' · '}{t('manager.modules.receiptReview.lineCount', { '0': `${review.lineCount}` }, `${review.lineCount} linjer`)}
                    </div>
                  </div>
                  {isPending && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleApprove(review); }}
                      disabled={approvingId === review.id}
                      className="flex items-center gap-2 px-4 py-2 min-h-[40px] bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                    >
                      {approvingId === review.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                      {t('manager.modules.receiptReview.approveButton', {}, 'Godkjenn og eksporter')}
                    </button>
                  )}
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {linesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                      </div>
                    ) : (
                      <>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-xs text-gray-500 uppercase">
                              <th className="px-4 py-2 text-left">{t('warehouse.general.itemNumber', {}, 'Varenr.')}</th>
                              <th className="px-4 py-2 text-left">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                              <th className="px-4 py-2 text-right">{t('manager.modules.receiptReview.orderedQty', {}, 'Bestilt')}</th>
                              <th className="px-4 py-2 text-right">{t('manager.modules.receiptReview.receivedQty', {}, 'Mottatt antall')}</th>
                              <th className="px-4 py-2 text-right">{t('manager.modules.receiptReview.remainingQty', {}, 'Rest')}</th>
                              <th className="px-4 py-2 text-left">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                              <th className="px-4 py-2 text-left">{t('warehouse.general.timestamp', {}, 'Tidspunkt')}</th>
                              {isPending && <th className="px-4 py-2 text-center">{t('manager.modules.receiptReview.adjust', {}, 'Juster')}</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {lines.map(line => (
                              <tr key={line.lineID} className={adjustedLineIds.has(line.lineID) ? 'bg-blue-50' : ''}>
                                <td className="px-4 py-2 font-medium text-gray-900">{line.itemNo}</td>
                                <td className="px-4 py-2 text-gray-700">{line.description1}</td>
                                <td className="px-4 py-2 text-right text-gray-600">
                                  {line.orderedQuantity ?? '-'}
                                </td>
                                <td className="px-4 py-2 text-right font-medium">
                                  {line.quantity}
                                  {line.originalQuantity !== null && line.originalQuantity !== line.quantity && (
                                    <span className="text-xs text-gray-400 line-through ml-2">{line.originalQuantity}</span>
                                  )}
                                </td>
                                <td className={`px-4 py-2 text-right ${line.remainingQuantity != null && line.remainingQuantity !== 0 ? 'font-medium text-orange-600' : 'text-gray-600'}`}>
                                  {line.remainingQuantity ?? '-'}
                                </td>
                                <td className="px-4 py-2 text-gray-600">{line.operatorID}</td>
                                <td className="px-4 py-2 text-gray-500">
                                  {line.transDt ? new Date(line.transDt).toLocaleString('nb-NO') : '-'}
                                </td>
                                {isPending && (
                                  <td className="px-4 py-2 text-center">
                                    <button
                                      onClick={() => {
                                        setAdjustingLine(line);
                                        setAdjustQty(String(line.quantity));
                                        setAdjustNote('');
                                      }}
                                      className="p-1.5 rounded hover:bg-orange-100 text-orange-600 min-h-[36px] min-w-[36px]"
                                      title={t('manager.modules.receiptReview.adjustTooltip', {}, 'Juster mottatt antall (logges)')}
                                    >
                                      <Pencil size={15} />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {adjustments.length > 0 && (
                          <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
                            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-800 mb-1">
                              <HistoryIcon size={12} />
                              {t('manager.modules.receiptReview.adjustmentLog', {}, 'Justeringslogg')}
                            </div>
                            {adjustments.map(adj => (
                              <div key={adj.id} className="text-xs text-blue-900">
                                {adj.itemNo}: {adj.originalQuantity} → {adj.adjustedQuantity}
                                {' · '}{adj.adjustedBy}{' · '}{new Date(adj.adjustedAt).toLocaleString('nb-NO')}
                                {adj.note && <span className="text-blue-700"> — {adj.note}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Justeringsmodal */}
      {adjustingLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {t('manager.modules.receiptReview.adjustTitle', {}, 'Juster mottatt antall')}
              </h3>
              <div className="text-sm text-gray-600 mt-0.5">
                {adjustingLine.itemNo} — {adjustingLine.description1}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.receiptReview.newQuantity', {}, 'Nytt antall')}
                </label>
                <NumericInput
                  mode="decimal"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-medium"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.receiptReview.noteOptional', {}, 'Merknad (valgfritt)')}
                </label>
                <input
                  type="text"
                  value={adjustNote}
                  onChange={(e) => setAdjustNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  maxLength={500}
                />
              </div>
              <p className="text-xs text-gray-500">
                {t('manager.modules.receiptReview.adjustInfo', {},
                  'Justeringen logges med hvem/når og oppdaterer det som eksporteres til ERP samt ordrens mottatt-antall.')}
              </p>
            </div>
            <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end gap-2">
              <button
                onClick={() => setAdjustingLine(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleAdjustConfirm}
                disabled={isSavingAdjust || adjustQty === '' || parseFloat(adjustQty) < 0}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingAdjust && <Loader2 size={14} className="animate-spin" />}
                {t('common.buttons.save', {}, 'Lagre')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptReview;
